import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {CatalogOtherField} from '@/components/catalog-other-field'
import {HelpIcon} from '@/components/help-icon'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {useApproveEmployee, type AprobacionEstructuracion} from '@/hooks/sgsst/riesgos/use-entrevista'
import {useEmployeeFicha} from '@/hooks/empresa/identidad/use-ficha'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {useTags} from '@/hooks/empresa/organizacion/use-tags'
import {translateApiError} from '@/lib/errors'

function Fact({label, value}: {label: string; value: string | null | undefined}) {
  return (
    <div className="space-y-1">
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">{label}</p>
      <p className="text-sm text-foreground">{value || '—'}</p>
    </div>
  )
}

/** UX O4: aprueba y ubica en un solo acto; el guard anti-escalamiento del backend es la fuente de verdad. */
export function AprobarSheet({
  businessId,
  employee,
  canAssignResponsable,
  onOpenChange,
  onApproved,
}: {
  businessId: string
  employee: Employee | null
  /** Refleja `_reject_role_escalation` del backend: nadie otorga un rol superior al suyo. */
  canAssignResponsable: boolean
  onOpenChange: (open: boolean) => void
  onApproved: () => void
}) {
  const {t} = useTranslation()
  const open = !!employee
  const employeeId = employee?.id
  const approve = useApproveEmployee(businessId)
  const {data: ficha} = useEmployeeFicha(businessId, open ? employeeId : undefined)
  const {data: areas} = useTags(businessId, 'areas', open)
  const {data: positions} = useTags(businessId, 'positions', open)
  const {data: sedes} = useTags(businessId, 'sedes', open)
  const {data: roster} = useAllEmployees(open ? businessId : undefined)

  const [area, setArea] = useState('')
  const [cargo, setCargo] = useState('')
  const [sedeName, setSedeName] = useState('')
  const [jefeId, setJefeId] = useState('')
  const [role, setRole] = useState<'user' | 'responsable'>('user')
  const [designar, setDesignar] = useState(false)
  const [touchedJefe, setTouchedJefe] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState<string | null>(null)

  // Siembra una vez por empleado abierto; `ready` guarda para cuál `employeeId` se sembró el estado local.
  if (open && employeeId && ready !== employeeId && ficha !== undefined) {
    setArea(ficha.area ?? '')
    setCargo(ficha.cargo ?? '')
    setSedeName(ficha.sede ?? '')
    setJefeId('')
    setRole('user')
    setDesignar(false)
    setTouchedJefe(false)
    setError(null)
    setReady(employeeId)
  }

  const activeMembers = useMemo(
    () => (roster ?? []).filter((e) => e.status === 'active' && e.id !== employeeId),
    [roster, employeeId]
  )

  // Sugiere como jefe al responsable del área hasta que el operador toque el selector.
  const areaResponsableId = useMemo(() => {
    if (!area) return null
    const norm = (s: string) => s.trim().toLowerCase()
    for (const e of roster ?? []) {
      const match = e.areas.find((a) => norm(a.name) === norm(area))
      if (match?.responsable_employee_id) return match.responsable_employee_id
    }
    return null
  }, [area, roster])

  const effectiveJefeId = touchedJefe ? jefeId : (areaResponsableId ?? jefeId)

  function handleOpenChange(next: boolean) {
    if (!next) {
      setReady(null)
      setError(null)
    }
    onOpenChange(next)
  }

  function run(estructuracion?: AprobacionEstructuracion) {
    if (!employeeId) return
    setError(null)
    approve.mutate(
      {employeeId, estructuracion},
      {
        onSuccess: () => {
          handleOpenChange(false)
          onApproved()
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  function approveAndPlace() {
    run({
      area: area || null,
      cargo: cargo || null,
      sede_id: sedeName ? (sedes?.items.find((s) => s.name === sedeName)?.id ?? null) : null,
      jefe_directo_employee_id: effectiveJefeId || null,
      role,
      designar_responsable_sgsst: role === 'responsable' && designar,
    })
  }

  const name = employee ? `${employee.name} ${employee.last_name}` : ''
  const multiSede = (sedes?.items.length ?? 0) > 1

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-[560px] overflow-y-auto" data-testid="aprobar-sheet">
        <DialogHeader>
          <DialogTitle>{t('employees.pendientes.sheet.title', {name})}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">{t('employees.pendientes.sheet.intro')}</p>

          <div className="space-y-2.5 rounded-lg border border-border bg-secondary/40 p-3">
            <div className="space-y-1">
              <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('employees.pendientes.sheet.resumenTitle')}
              </p>
              <p className="text-xs text-muted-foreground">{t('employees.pendientes.sheet.resumenHint')}</p>
            </div>
            {ficha && (ficha.area || ficha.cargo || ficha.sede || ficha.tipo_vinculacion || ficha.fecha_ingreso) ? (
              <div className="grid grid-cols-2 gap-3">
                <Fact label={t('employees.pendientes.sheet.fields.cargoDeclarado')} value={ficha.cargo} />
                <Fact label={t('employees.pendientes.sheet.fields.areaDeclarada')} value={ficha.area} />
                <Fact label={t('employees.pendientes.sheet.fields.sedeDeclarada')} value={ficha.sede} />
                <Fact label={t('employees.pendientes.sheet.fields.vinculacion')} value={ficha.tipo_vinculacion} />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="aprobar-ficha-pendiente">
                {t('employees.pendientes.sheet.fichaPendiente')}
              </p>
            )}
          </div>

          <div className="space-y-4">
            <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('employees.pendientes.sheet.ubicacionTitle')}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <CatalogOtherField
                id="aprobar-area"
                label={t('employees.pendientes.sheet.area')}
                options={(areas?.items ?? []).map((a) => a.name)}
                value={area}
                onChange={setArea}
                onCommit={setArea}
              />
              <CatalogOtherField
                id="aprobar-cargo"
                label={t('employees.pendientes.sheet.cargo')}
                options={(positions?.items ?? []).map((p) => p.name)}
                value={cargo}
                onChange={setCargo}
                onCommit={setCargo}
              />
            </div>

            {multiSede && (
              <CatalogOtherField
                id="aprobar-sede"
                label={t('employees.pendientes.sheet.sede')}
                options={(sedes?.items ?? []).map((s) => s.name)}
                value={sedeName}
                onChange={setSedeName}
                onCommit={setSedeName}
              />
            )}

            <div className="space-y-2">
              <Label htmlFor="aprobar-jefe">{t('employees.pendientes.sheet.jefe')}</Label>
              <EntitySearchSelect
                id="aprobar-jefe"
                items={activeMembers}
                value={activeMembers.find((e) => e.id === effectiveJefeId) ?? null}
                getKey={(e) => e.id}
                filterText={(e) => `${e.name} ${e.last_name} ${e.cedula}`}
                renderOption={(e) => (
                  <span>
                    {e.name} {e.last_name}
                  </span>
                )}
                renderTriggerValue={(e) => (
                  <span>
                    {e.name} {e.last_name}
                  </span>
                )}
                placeholder={t('employees.pendientes.sheet.jefeNinguno')}
                searchPlaceholder={t('employees.pendientes.sheet.jefeSearchPlaceholder')}
                noResultsLabel={t('employees.pendientes.sheet.jefeNoResults')}
                clearLabel={t('employees.pendientes.sheet.jefeNinguno')}
                testId="aprobar-jefe"
                onPick={(e) => {
                  setTouchedJefe(true)
                  setJefeId(e?.id ?? '')
                }}
              />
              {!touchedJefe && areaResponsableId && (
                <p className="text-xs text-muted-foreground">{t('employees.pendientes.sheet.jefeSugeridoHint')}</p>
              )}
            </div>

            <div className="space-y-2">
              <span className="flex items-center gap-1.5">
                <Label htmlFor="aprobar-role">{t('employees.pendientes.sheet.role')}</Label>
                <HelpIcon label={t('employees.pendientes.sheet.rolTipLabel')} testId="aprobar-role-help">
                  {t('employees.pendientes.sheet.roleDesignacionHint')}
                </HelpIcon>
              </span>
              <select
                id="aprobar-role"
                data-testid="aprobar-role"
                value={role}
                onChange={(e) => {
                  const next = e.target.value as 'user' | 'responsable'
                  setRole(next)
                  if (next !== 'responsable') setDesignar(false)
                }}
                className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                <option value="user">{t('roles.user')}</option>
                <option value="responsable" disabled={!canAssignResponsable}>
                  {t('roles.responsable')}
                </option>
              </select>
              {role === 'responsable' && (
                <div className="space-y-2">
                  <Tag tone="amber">{t('employees.pendientes.sheet.roleResponsableHint')}</Tag>
                  {canAssignResponsable ? (
                    <label className="flex cursor-pointer items-start gap-2" data-testid="aprobar-designar-label">
                      <Checkbox
                        checked={designar}
                        onCheckedChange={(v) => setDesignar(v === true)}
                        data-testid="aprobar-designar"
                        className="mt-0.5"
                      />
                      <span className="text-xs text-foreground">
                        {t('employees.pendientes.sheet.designarResponsable')}
                        <span className="mt-0.5 block text-muted-foreground">
                          {t('employees.pendientes.sheet.designarResponsableHint')}
                        </span>
                      </span>
                    </label>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t('employees.pendientes.sheet.roleDesignacionHint')}
                    </p>
                  )}
                </div>
              )}
              {!canAssignResponsable && (
                <p className="text-xs text-muted-foreground" data-testid="aprobar-role-blocked">
                  {t('employees.pendientes.sheet.roleEscalationBlocked')}
                </p>
              )}
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="space-y-2">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                data-testid="aprobar-sin-ajustar"
                disabled={approve.isPending}
                onClick={() => run(undefined)}>
                {t('employees.pendientes.sheet.submitFastPath')}
              </Button>
              <Button
                type="button"
                data-testid="aprobar-y-ubicar"
                disabled={approve.isPending}
                onClick={approveAndPlace}>
                {t('employees.pendientes.sheet.submit')}
              </Button>
            </div>
            <p className="text-right text-xs text-muted-foreground">{t('employees.pendientes.sheet.fastPathHint')}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
